/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * AccountDisabled - shown in place of the app shell when an authenticated user's
 * account has been disabled. The server already blocks API access for disabled
 * accounts; this gives the (still token-holding) user immediate, clear feedback
 * instead of a confusing wall of failed requests until their token expires.
 */

import { useState } from 'react'
import { ShieldAlert, Loader2 } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

export default function AccountDisabled() {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const [loggingOut, setLoggingOut] = useState(false)

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      // The auth store's logout() swallows its own /auth/logout failure and clears local state
      // unconditionally, so it resolves even though that endpoint now returns 403 ACCOUNT_DISABLED
      // for this user. Once the user is cleared the protected route unmounts this component and
      // /login renders, so we intentionally do NOT reset state on the success path (that would be a
      // setState on an unmounted component). Only re-enable the button if logout somehow rejects.
      await logout()
    } catch {
      setLoggingOut(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
          <ShieldAlert className="h-7 w-7 text-red-600 dark:text-red-400" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Your account has been disabled
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Access to this account is currently suspended. If you believe this is a mistake, please
          contact support for assistance.
        </p>
        {user?.disabledReason && (
          <p className="mt-3 rounded-md bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            <strong>Reason:</strong> {user.disabledReason}
          </p>
        )}
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 dark:bg-gray-100 px-5 py-2.5 text-sm font-medium text-white dark:text-gray-900 hover:opacity-90 disabled:opacity-60"
        >
          {loggingOut && <Loader2 className="h-4 w-4 animate-spin" />}
          Log out
        </button>
      </div>
    </div>
  )
}
